import React, { useEffect, useState } from "react";
import { api, CATEGORY_LABELS } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, ShieldCheck, Search, Bell, Plus, Loader2, Send, IndianRupee, Zap } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const badgeFor = (s) => ({
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
}[s] || "");

/* ---------------------------------------------------------------------------
 * New Enrollment Modal — BK Teacher OTP flow wired to /api/verification/bk-otp
 * ------------------------------------------------------------------------- */
function NewEnrollmentModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    applicant_name: "", phone: "", email: "", age: "", gender: "",
    category: "bk_student", pin_code: "", city: "", state: "",
    bk_centre_uid: "", bk_center_email: "", insurance_unique_id: "",
  });
  const [otp, setOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState("");

  const isTeacher = form.category === "bk_teacher_insured" || form.category === "bk_teacher_no_insurance";
  const isInsured = form.category === "bk_teacher_insured";

  // Emails that bypass the @bkivv.org gate on the frontend (kept in sync with
  // the BK_CENTRE_EMAIL_WHITELIST env on the backend). Owner + support inboxes.
  const EMAIL_WHITELIST = new Set(["info@medishiv.com"]);
  const isEmailAllowed = (e) => {
    const em = (e || "").trim().toLowerCase();
    return em.endsWith("@bkivv.org") || EMAIL_WHITELIST.has(em);
  };

  // If email changes after verification, invalidate it — enrollment blocked.
  useEffect(() => {
    if (emailVerified && form.bk_center_email !== verifiedEmail) {
      setEmailVerified(false);
    }
  }, [form.bk_center_email, emailVerified, verifiedEmail]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendOtp = async () => {
    const email = (form.bk_center_email || "").trim().toLowerCase();
    if (!email.endsWith("@bkivv.org")) {
      toast.error("Email must end with @bkivv.org");
      return;
    }
    setBusy("otp-send");
    try {
      const { data } = await api.post("/verification/bk-otp/request", {
        email, applicant_name: form.applicant_name || undefined,
      });
      setCooldown(data?.resend_available_in_seconds || 60);
      if (data?.dev_otp) {
        toast.success(`OTP: ${data.dev_otp} — whitelisted admin bypass (email delivery blocked)`, { duration: 15000 });
        setOtp(String(data.dev_otp));
      } else {
        toast.success(`OTP sent to ${email} — please check the BK Centre inbox.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send OTP"); }
    finally { setBusy(""); }
  };

  const verifyOtpNow = async () => {
    const email = (form.bk_center_email || "").trim().toLowerCase();
    if (!/^\d{6}$/.test(otp)) { toast.error("OTP must be 6 digits"); return; }
    setBusy("otp-verify");
    try {
      await api.post("/verification/bk-otp/verify", { email, otp });
      setEmailVerified(true);
      setVerifiedEmail(email);
      toast.success("BK Centre email verified");
    } catch (e) { toast.error(e?.response?.data?.detail || "OTP verification failed"); }
    finally { setBusy(""); }
  };

  const canSubmit = () => {
    // v1.62 — PIN no longer required at enrollment; medicines ship pan-India
    // and lab bookings collect PIN inside the WA bot's Health & Lab flow.
    if (!form.applicant_name || !form.phone) return false;
    if (isTeacher) {
      if (!form.bk_centre_uid) return false;
      if (!isEmailAllowed(form.bk_center_email)) return false;
      if (!emailVerified) return false;
      if (isInsured && !form.insurance_unique_id) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) { toast.error("Please complete all mandatory fields and OTP verification."); return; }
    setBusy("submit");
    try {
      const payload = {
        applicant_name: form.applicant_name, phone: form.phone,
        email: form.email || undefined, age: form.age ? Number(form.age) : undefined,
        gender: form.gender || undefined, category: form.category,
        insurance_eligible: isInsured, pin_code: form.pin_code,
        city: form.city || undefined, state: form.state || undefined,
      };
      if (isTeacher) {
        payload.bk_centre_uid = form.bk_centre_uid;
        payload.bk_center_email = form.bk_center_email.toLowerCase();
      }
      if (isInsured) {
        payload.insurance_unique_id = form.insurance_unique_id;
        payload.bk_teacher_id = form.insurance_unique_id;
      }
      const { data } = await api.post("/enrollments", payload);
      toast.success(`Enrollment created (${data.id})`);
      onCreated?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create enrollment"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Enrollment</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Full Name *</label>
              <Input value={form.applicant_name} onChange={(e) => setField("applicant_name", e.target.value)} data-testid="enr-applicant-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Mobile *</label>
              <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="enr-phone" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Category *</label>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger data-testid="enr-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bk_student">BK Student</SelectItem>
                  <SelectItem value="bk_teacher_no_insurance">BK Teacher (Non-Insured)</SelectItem>
                  <SelectItem value="bk_teacher_insured">BK Teacher (Insured)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">PIN Code *</label>
              <Input value={form.pin_code} onChange={(e) => setField("pin_code", e.target.value)} data-testid="enr-pin" />
            </div>
            <div>
              <label className="text-xs text-slate-500">City</label>
              <Input value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="enr-city" />
            </div>
            <div>
              <label className="text-xs text-slate-500">State</label>
              <Input value={form.state} onChange={(e) => setField("state", e.target.value)} data-testid="enr-state" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Age</label>
              <Input type="number" value={form.age} onChange={(e) => setField("age", e.target.value)} data-testid="enr-age" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Gender</label>
              <Input value={form.gender} onChange={(e) => setField("gender", e.target.value)} data-testid="enr-gender" />
            </div>
          </div>

          {isTeacher && (
            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardContent className="p-3 space-y-3">
                <div className="text-sm font-semibold text-emerald-800">BK Teacher Verification</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">BK Centre UID *</label>
                    <Input value={form.bk_centre_uid} onChange={(e) => setField("bk_centre_uid", e.target.value)} data-testid="enr-centre-uid" placeholder="e.g. BK-CENTRE-001" />
                    <div className="text-[10px] text-slate-500 mt-1">Not unique — multiple teachers can share one UID.</div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">BK Centre Email * (@bkivv.org)</label>
                    <Input value={form.bk_center_email} onChange={(e) => setField("bk_center_email", e.target.value.toLowerCase())} data-testid="enr-centre-email" placeholder="centre@bkivv.org" />
                  </div>
                </div>
                <div className="flex gap-2 items-end flex-wrap">
                  <Button size="sm" onClick={sendOtp} disabled={busy === "otp-send" || cooldown > 0 || !isEmailAllowed(form.bk_center_email)} data-testid="enr-send-otp">
                    {busy === "otp-send" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Send OTP"}
                  </Button>
                  <div className="flex-1">
                    <label className="text-xs text-slate-500">Enter 6-digit OTP</label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="enr-otp-input" placeholder="000000" />
                  </div>
                  <Button size="sm" onClick={verifyOtpNow} disabled={busy === "otp-verify" || otp.length !== 6} data-testid="enr-verify-otp" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {busy === "otp-verify" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Verify
                  </Button>
                  {emailVerified ? (
                    <Badge className="bg-emerald-100 text-emerald-800" data-testid="enr-email-verified-badge"><ShieldCheck size={12} className="mr-1" /> Verified</Badge>
                  ) : (
                    <Badge variant="outline" data-testid="enr-email-notverified-badge">Not Verified</Badge>
                  )}
                </div>
                {isInsured && (
                  <div>
                    <label className="text-xs text-slate-500">Insurance Unique ID *</label>
                    <Input value={form.insurance_unique_id} onChange={(e) => setField("insurance_unique_id", e.target.value)} data-testid="enr-insurance-id" placeholder="Health & Happiness Card No." />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="enr-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy === "submit" || !canSubmit()} data-testid="enr-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy === "submit" ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create Enrollment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Enrollments() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveMarkPaid, setApproveMarkPaid] = useState(false);
  const [approving, setApproving] = useState(false);
  // v1.60 — per-category auto-approve toggles
  const [autoFlags, setAutoFlags] = useState({
    bk_student: false, bk_teacher_no_insurance: false, bk_teacher_insured: false,
  });
  const [flagsBusy, setFlagsBusy] = useState(false);

  const load = async () => {
    const params = tab === "all" ? {} : { status: tab };
    if (q) params.q = q;
    const r = await api.get("/enrollments", { params });
    setItems(r.data);
  };

  const loadFlags = async () => {
    try {
      const r = await api.get("/settings/enrollment-auto-approve");
      setAutoFlags(r.data?.flags || {});
    } catch (e) { /* non-fatal — panel just stays defaulted */ }
  };

  const toggleFlag = async (key) => {
    setFlagsBusy(true);
    const next = { ...autoFlags, [key]: !autoFlags[key] };
    try {
      const r = await api.put("/settings/enrollment-auto-approve", { flags: next });
      setAutoFlags(r.data?.flags || next);
      toast.success(`${CATEGORY_LABELS?.[key] || key} auto-approve ${next[key] ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save toggle");
    } finally {
      setFlagsBusy(false);
    }
  };

  const markPaymentReceived = async (it) => {
    try {
      const r = await api.post(`/enrollments/${it.id}/payment-received`, { received: true });
      if (r.data?.auto_approved) {
        toast.success("Payment received · auto-approved · WA congrats sent");
      } else {
        toast.success("Payment marked as received");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark payment");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadFlags(); }, [tab, q]);

  // Auto-refresh every 15s so new WA-bot enrollments appear without a manual
  // reload. Pauses when the browser tab is hidden to save bandwidth + rate.
  useEffect(() => {
    let iv = null;
    const tick = () => { if (!document.hidden) load(); };
    iv = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (iv) clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q]);

  const openApprove = (it) => {
    setApproveTarget(it);
    setApproveMarkPaid(false);
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      const payload = { status: "approved" };
      if (approveTarget.category === "bk_student" && approveMarkPaid) {
        payload.mark_paid = true;
      }
      await api.post(`/enrollments/${approveTarget.id}/decision`, payload);
      toast.success(approveMarkPaid
        ? "Enrollment approved · ₹1499 marked as received"
        : "Enrollment approved");
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setApproving(false);
    }
  };

  const decide = async (id, status) => {
    try {
      await api.post(`/enrollments/${id}/decision`, { status });
      toast.success(`Enrollment ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const notify = async (id) => {
    try {
      const r = await api.post(`/notify/enrollment/${id}`, {});
      const sim = r.data?.wa_response?.simulated;
      toast.success(sim ? "Notification simulated (no WA creds)" : "WhatsApp status update sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notification failed");
    }
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps text-accent mb-2">Enrollments</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Membership applications</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Review new sign-ups, approve to auto-create a member, or reject with a note.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="enr-new-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus size={14} /> New Enrollment
        </Button>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="enrollments-tabs" className="bg-secondary h-auto p-1">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-4 border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input data-testid="enrollment-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by mobile number, name, email or PIN…" className="pl-10 h-11 rounded-lg" />
        </div>
      </Card>

      <Card className="p-4 border-border bg-gradient-to-br from-indigo-50/60 to-white" data-testid="auto-approve-panel">
        <div className="flex items-start gap-3 mb-3">
          <Zap size={18} className="text-indigo-600 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-indigo-900">Auto-approve new enrollments</div>
            <div className="text-xs text-muted-foreground mt-0.5">Turn on per category. Enrollments that meet the safety prerequisites will be approved automatically — WA congrats + member creation fire the same as a manual approve.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-100 bg-white/80 cursor-pointer" data-testid="auto-approve-bk_student">
            <Switch checked={!!autoFlags.bk_student} onCheckedChange={() => !flagsBusy && toggleFlag("bk_student")} data-testid="auto-approve-switch-bk_student" />
            <div className="flex-1">
              <div className="text-sm font-medium">BK Student</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Fires immediately at enrollment (v1.62 — enrollment is now free; ₹1499 only if student books Full Body Checkup).</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-100 bg-white/80 cursor-pointer" data-testid="auto-approve-bk_teacher_no_insurance">
            <Switch checked={!!autoFlags.bk_teacher_no_insurance} onCheckedChange={() => !flagsBusy && toggleFlag("bk_teacher_no_insurance")} data-testid="auto-approve-switch-bk_teacher_no_insurance" />
            <div className="flex-1">
              <div className="text-sm font-medium">BK Teacher (Non-Insured)</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Fires immediately at enrollment (BK Centre email OTP already required to submit).</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-100 bg-white/80 cursor-pointer" data-testid="auto-approve-bk_teacher_insured">
            <Switch checked={!!autoFlags.bk_teacher_insured} onCheckedChange={() => !flagsBusy && toggleFlag("bk_teacher_insured")} data-testid="auto-approve-switch-bk_teacher_insured" />
            <div className="flex-1">
              <div className="text-sm font-medium">BK Teacher (Insured)</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Fires when BOTH email OTP is verified AND the insurance number matches the roster.</div>
            </div>
          </label>
        </div>
      </Card>

      <Card className="border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Applicant</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Centre UID</TableHead>
              <TableHead>Centre Email</TableHead>
              <TableHead>Insurance ID</TableHead>
              <TableHead>PIN</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(items || []).map((it) => (
              <TableRow key={it.id}>
                <TableCell className="font-medium">{it.applicant_name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{it.phone}</TableCell>
                <TableCell><Badge variant="outline">{CATEGORY_LABELS?.[it.category] || it.category}</Badge></TableCell>
                <TableCell className="text-xs">{it.bk_centre_uid || "—"}</TableCell>
                <TableCell className="text-xs">
                  {it.bk_center_email ? (
                    <div className="flex items-center gap-1">
                      <span>{it.bk_center_email}</span>
                      {it.bk_center_email_verified && <ShieldCheck size={12} className="text-emerald-600" />}
                    </div>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-xs">{it.insurance_unique_id || it.bk_teacher_id || "—"}</TableCell>
                <TableCell className="text-xs">{it.pin_code}</TableCell>
                <TableCell><Badge className={badgeFor(it.status)}>{it.status}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  {it.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => openApprove(it)} data-testid={`enr-approve-${it.id}`}>
                        <Check size={14} /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(it.id, "rejected")} data-testid={`enr-reject-${it.id}`}>
                        <X size={14} /> Reject
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => notify(it.id)} data-testid={`enr-notify-${it.id}`}>
                    <Bell size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(!items || items.length === 0) && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground p-8">No enrollments yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <NewEnrollmentModal open={showNew} onClose={() => setShowNew(false)} onCreated={load} />

      <Dialog open={!!approveTarget} onOpenChange={(v) => !v && !approving && setApproveTarget(null)}>
        <DialogContent data-testid="enr-approve-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve enrollment</DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                You're about to approve{" "}
                <span className="font-semibold text-foreground">{approveTarget.applicant_name}</span>{" "}
                ({approveTarget.phone}) as{" "}
                <Badge variant="outline">{CATEGORY_LABELS?.[approveTarget.category] || approveTarget.category}</Badge>.
                A member record + WhatsApp confirmation will be created.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={approving} data-testid="enr-approve-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmApprove}
              disabled={approving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="enr-approve-confirm"
            >
              {approving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
              Confirm approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
